import * as React from "react";
import {
  SocialProcurement,
  SocialProcurementLineItem,
} from "@interfaces/socialProcurement";
import {
  decodeSocialProcurement,
  encodeSocialProcurement,
} from "decoders/socialProcurement";
import {
  deleteBatchApiData,
  getApiData,
  listApiData,
  postApiData,
  postBatchApiData,
} from "@hooks/utils/api";

export const useSocialProcurement = (
  projectId?: string,
  packageId?: string,
  reportId?: string
) => {
  const [socialProcurements, setSocialProcurements] = React.useState<
    SocialProcurementLineItem[]
  >([]);
  const [isSocialProcurementLoading, setIsLoading] =
    React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const getSocialProcurement = async (
    id: string
  ): Promise<SocialProcurementLineItem | undefined> => {
    try {
      setIsLoading(true);
      const socialProcurementResult = await getApiData(
        "getSocialProcurement",
        "socialProcurement",
        id
      );
      if (Object.keys(socialProcurementResult.data).length === 0) {
        return undefined;
      }
      return socialProcurementResult
        ? decodeSocialProcurement(
            socialProcurementResult.data as SocialProcurement
          )
        : undefined;
    } catch (e: any) {
      setError("Could not fetch socialProcurement");
    } finally {
      setIsLoading(false);
    }
  };

  const listSocialProcurements = async (
    socialProcurementId: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const socialProcurementResult = await listApiData(
        "listSocialProcurements",
        "socialProcurement",
        socialProcurementId
      );
      setSocialProcurements(
        socialProcurementResult.data.map((p) =>
          decodeSocialProcurement(p as SocialProcurement)
        )
      );
    } catch (e: any) {
      setError("Could not list socialProcurements");
    } finally {
      setIsLoading(false);
    }
  };

  const searchSocialProcurements = async (
    query: string
  ): Promise<SocialProcurementLineItem[]> => {
    try {
      const socialProcurementResult = await listApiData(
        "searchSocialProcurements",
        "socialProcurement",
        query
      );
      return socialProcurementResult.data.map((p) =>
        decodeSocialProcurement(p as SocialProcurement)
      );
    } catch (e: any) {
      setError("Could not search socialProcurements");
      return [];
    }
  };

  const createSocialProcurement = async (
    socialProcurement: SocialProcurementLineItem
  ): Promise<SocialProcurementLineItem> => {
    try {
      setIsLoading(true);
      const encodedSocialProcurement =
        encodeSocialProcurement(socialProcurement);
      const upsertedSocialProcurement = await postApiData(
        "createSocialProcurement",
        "socialProcurement",
        encodedSocialProcurement
      );
      const decodedSocialProcurement = decodeSocialProcurement(
        upsertedSocialProcurement.data as SocialProcurement
      );
      setSocialProcurements([...socialProcurements, decodedSocialProcurement]);
      return decodedSocialProcurement;
    } catch (e: any) {
      setError("Could not list socialProcurements");
    } finally {
      setIsLoading(false);
    }
    return socialProcurement;
  };

  const updateSocialProcurement = async (
    socialProcurement: SocialProcurementLineItem
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedSocialProcurement =
        encodeSocialProcurement(socialProcurement);
      await postApiData(
        "updateSocialProcurement",
        "socialProcurement",
        encodedSocialProcurement
      );
      setSocialProcurements(
        socialProcurements.map((c) => {
          if (c.id === socialProcurement.id) {
            return socialProcurement;
          }
          return c;
        })
      );
    } catch (e: any) {
      setError("Could not update socialProcurement");
    } finally {
      setIsLoading(false);
    }
  };

  const upsertSocialProcurements = async (
    socialProcurementsToUpsert: SocialProcurementLineItem[]
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedSocialProcurements = socialProcurementsToUpsert.map((e) =>
        encodeSocialProcurement(e)
      );
      await postBatchApiData(
        "upsertSocialProcurements",
        "socialProcurement",
        encodedSocialProcurements
      );

      const updatedSocialProcurements = socialProcurements.reduce(
        (acc, socialProcurement) => {
          const updatedSocialProcurement = socialProcurementsToUpsert.find(
            (e) => e.id === socialProcurement.id
          );
          if (updatedSocialProcurement) {
            return [...acc, updatedSocialProcurement];
          }
          return [...acc, socialProcurement];
        },
        [] as SocialProcurementLineItem[]
      );

      const createdSocialProcurement = socialProcurementsToUpsert.filter(
        (sp) => {
          return !updatedSocialProcurements.find((s) => s.id === sp.id);
        }
      );
      setSocialProcurements([
        ...updatedSocialProcurements,
        ...createdSocialProcurement,
      ]);
    } catch (e: any) {
      setError("Could not update socialProcurement");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSocialProcurements = async (
    socialProcurementsToDelete: SocialProcurementLineItem[]
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedSocialProcurement = socialProcurementsToDelete.map((e) =>
        encodeSocialProcurement(e)
      );
      await deleteBatchApiData(
        "deleteSocialProcurements",
        "socialProcurement",
        encodedSocialProcurement
      );

      const deletedRowIds = socialProcurementsToDelete.map((e) => e.id);
      setSocialProcurements(
        socialProcurements.filter((e) => {
          return !deletedRowIds.includes(e.id);
        })
      );
    } catch (e: any) {
      setError("Could not delete socialProcurement");
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (projectId) {
      listSocialProcurements(projectId);
    }
  }, [projectId]);

  React.useEffect(() => {
    if (packageId) {
      listSocialProcurements(packageId);
    }
  }, [packageId]);

  React.useEffect(() => {
    if (reportId) {
      listSocialProcurements(reportId);
    }
  }, [reportId]);

  return {
    socialProcurements,
    getSocialProcurement,
    listSocialProcurements,
    createSocialProcurement,
    updateSocialProcurement,
    deleteSocialProcurements,
    upsertSocialProcurements,
    searchSocialProcurements,
    isSocialProcurementLoading,
    error,
  };
};
